import { type RefObject, useEffect, useRef, useState } from 'react';

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    callback: (now: number, metadata: unknown) => void,
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/**
 * Estimates delivered camera FPS from the preview video element.
 * Uses requestVideoFrameCallback when available; otherwise returns null.
 */
export function useDeliveredFps(
  videoRef: RefObject<HTMLVideoElement | null>,
  enabled: boolean,
): number | null {
  const [fps, setFps] = useState<number | null>(null);
  const frameTimesRef = useRef<number[]>([]);

  useEffect(() => {
    if (!enabled) {
      frameTimesRef.current = [];
      setFps(null);
      return;
    }

    const video = videoRef.current as VideoWithFrameCallback | null;
    if (!video || typeof video.requestVideoFrameCallback !== 'function') {
      setFps(null);
      return;
    }

    let cancelled = false;
    let handle = 0;

    const onFrame = (now: number) => {
      if (cancelled) return;

      const times = frameTimesRef.current;
      times.push(now);
      while (times.length > 0 && now - times[0] > 1000) {
        times.shift();
      }

      if (times.length >= 2) {
        const span = times[times.length - 1] - times[0];
        if (span > 0) {
          setFps(((times.length - 1) / span) * 1000);
        }
      }

      handle = video.requestVideoFrameCallback!(onFrame);
    };

    handle = video.requestVideoFrameCallback(onFrame);

    return () => {
      cancelled = true;
      frameTimesRef.current = [];
      if (typeof video.cancelVideoFrameCallback === 'function') {
        video.cancelVideoFrameCallback(handle);
      }
    };
  }, [enabled, videoRef]);

  return fps;
}
